import {NavigationTab} from '@/logged/features';
import {useNotifications} from '@/logged/features/notifications';
import {useUser} from '@/logged/features/user';
import {I18nService} from '@malt/vue3-i18n';
import {computed, ref} from 'vue';
import {CachedFilteredTab, useFilteredTabs} from '../useFilteredTabs';
import {NavigationTabsRecord} from './navigation.types';

export function useFreelanceNavigationTabs(): NavigationTabsRecord {
    const userStore = useUser();
    const notificationsStore = useNotifications(); // TODO

    const currentLanguage = computed(() => userStore.user?.currentLanguage);
    const logoNavigationRoute = computed(() => '/dashboard/freelancer');

    const allNavigationRoutes = computed((): NavigationTab[] => [
        {
            id: 'FreelancerDashboard',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.dashboard'),
            url: {href: '/dashboard/freelancer'},
            icons: {mainIcon: 'aside/dashboard'},
            isActiveWhen: ({startWith}) => startWith('/dashboard/freelancer'),
        },
        {
            id: 'FreelancerInbox',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.inbox'),
            icons: {mainIcon: 'aside/inbox'},
            url: {href: '/messages'},
            isActiveWhen: ({startWith}) => startWith('/messages'),
        },
        {
            id: 'FreelancerEnhanceMyProfile',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.enhance.my.profile'),
            icons: {mainIcon: 'aside/profil', canBeActive: false},
            children: [
                {
                    id: 'FreelancerViewProfile',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.view.profile'),
                    url: {href: '/profile'},
                    isActiveWhen: ({startWith}) => startWith('/profile'),
                },
                {
                    id: 'FreelancerStats',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.stats'),
                    url: {href: '/analytics/views'},
                    isActiveWhen: ({startWith}) => startWith('/analytics/views'),
                },
                {
                    id: 'FreelancerProjectPreferences',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.project.preferences'),
                    url: {href: '/mission-preferences'},
                    isActiveWhen: ({startWith}) => startWith('/mission-preferences'),
                },
            ],
        },
        {
            id: 'FreelancerManageProjects',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.manage.projects'),
            url: {href: '/missions'},
            icons: {mainIcon: 'aside/manage-projects'},
            isActiveWhen: ({startWith}) => startWith('/missions'),
        },
        {
            id: 'FreelancerInvoicesPayments',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.invoices.and.payments'),
            url: {href: '/company/payment_in/iban'},
            icons: {mainIcon: 'aside/invoice'},
            isActiveWhen: ({startWith}) => startWith('/company/payment_in') || startWith('/company-wallet'),
        },
        {
            separator: 'top',
            id: 'FreelancerCompany',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.company'),
            url: {href: '/company'},
            icons: {mainIcon: 'aside/my-company', canBeActive: false},
            isActiveWhen: ({startWith}) => startWith('/company'),
        },
        {
            id: 'FreelancerResources',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.resources'),
            icons: {mainIcon: 'aside/resources', canBeActive: false},
            children: [
                {
                    id: 'FreelancerResourcesCenter',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.resources.center'),
                    url: {href: `https://resources.malt.com/fr/freelances/`, blank: true},
                },
                {
                    id: 'FreelancerDailyRateTracker',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.daily.rate.tracker'),
                    constraint: currentLanguage.value === 'fr',
                    url: {href: '/t/barometre-tarifs'},
                    isActiveWhen: ({startWith}) => startWith('/t/barometre-tarifs'),
                },
                {
                    id: 'FreelancerPartners',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.partners'),
                    url: {href: '/c/partenaires-malt'},
                    isActiveWhen: ({startWith}) => startWith('/c/partenaires-malt'),
                },
                {
                    id: 'FreelancerHelpCenter',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.help.center'),
                    url: {href: `https://help.malt.com/kb/${currentLanguage.value}/18216`, blank: true, heapId: `navbar-tab__freelance-help_center`},
                },
            ],
        },
        {
            id: 'FreelancerSettings',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.settings'),
            url: {href: '/account'},
            icons: {mainIcon: 'aside/settings'},
            isActiveWhen: ({startWith}) => startWith('/account'),
        },
    ]);

    // Can't use computed for persisting state
    const cachedNavigationRoutes = ref<CachedFilteredTab[]>([]);

    const {filteredNavigationRoutes} = useFilteredTabs(cachedNavigationRoutes, allNavigationRoutes);

    return {
        logoNavigationRoute,
        allNavigationRoutes,
        filteredNavigationRoutes,
        cachedNavigationRoutes,
    };
}
